import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { FetchParams } from '@/js/utils/fetch-params'
import apiClient from '../api/api-client'
import { AxiosResponse } from 'axios'

import { Module, ModuleCollection } from '../model'
import { useLoading } from '@/js/utils/useLoading'

export const useModuleStore = defineStore('module', () => {
    const { isLoading, isMethodLoading, setLoading, errors, hasError } = useLoading()

    const modules = ref<Array<Module>>([])
    const totalItems = ref<number>(0)
    const getModules = computed(() => modules)
    const getModuleById = computed(() => {
        return (modulId: string) => modules.value.find((module) => module.modulId === modulId)
    })

    const getModulesByStudiengangId = computed(() => {
        return (studiengangId: string) =>
            modules.value.filter((modul) => modul.studiengangId === studiengangId)
    })

    const replaceModuleInState = (newModule: Module) => {
        modules.value = [
            ...modules.value.filter((module) => module.modulId !== newModule.modulId),
            newModule
        ]
    }

    async function fetchModules(): Promise<void> {
        await setLoading('fetchingModules', async () => {
            modules.value = []
            const url = '/api/module'
            const response: AxiosResponse<ModuleCollection, any> = await apiClient.get(url, {
                params: {
                    pagination: false
                }
            })
            modules.value = response.data['hydra:member']
            totalItems.value = response.data['hydra:totalItems'] ?? 0
        })
    }

    async function fetchModulesByModulhandbuchId(modulhandbuchId: string): Promise<void> {
        await setLoading('fetchingModules', async () => {
            modules.value = []
            const url = '/api/module'
            const response: AxiosResponse<ModuleCollection, any> = await apiClient.get(url, {
                params: {
                    pagination: false,
                    modulhandbuchId
                }
            })
            modules.value = response.data['hydra:member']
            totalItems.value = response.data['hydra:totalItems'] ?? 0
        })
    }

    async function fetchPaginatedModules(
        params: FetchParams
    ): Promise<AxiosResponse<ModuleCollection, any>> {
        const url = '/api/module'

        return await apiClient.get(url, {
            params
        })
    }

    async function saveModulverantwortliche(
        modulId: string,
        modulverantwortlicheIds: Array<string>
    ): Promise<void> {
        await setLoading('saveModulverantwortliche', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.put(
                '/api/module/change_modulverantwortliche',
                {
                    modul: `/api/module/${modulId}`,
                    modulverantwortliche: modulverantwortlicheIds
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveLehrende(modulId: string, lehrendeIds: Array<string>): Promise<void> {
        await setLoading('savingLehrende', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.put(
                '/api/module/change_lehrende',
                {
                    modul: `/api/module/${modulId}`,
                    lehrende: lehrendeIds
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveVeranstaltungsOrt(modulId: string, newOrt: string): Promise<void> {
        await setLoading('savingVeranstaltungsOrt', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_ort',
                {
                    modul: `/api/module/${modulId}`,
                    veranstaltungsOrt: newOrt
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveModulDauer(modulId: string, newModulDauer: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_moduldauer',
                {
                    modul: `/api/module/${modulId}`,
                    modulDauer: newModulDauer
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveBenotung(modulId: string, newBenotung: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_benotung',
                {
                    modul: `/api/module/${modulId}`,
                    benotung: newBenotung
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveZwingendeVoraussetzungen(
        modulId: string,
        newVoraussetzung: string
    ): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_voraussetzungen_zwingend',
                {
                    modul: `/api/module/${modulId}`,
                    zwingendeVoraussetzungen: newVoraussetzung
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveEmpfohleneVoraussetzungen(
        modulId: string,
        newVoraussetzung: string
    ): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_voraussetzungen_empfohlen',
                {
                    modul: `/api/module/${modulId}`,
                    empfohleneVoraussetzungen: newVoraussetzung
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveLernziele(modulId: string, newLernziele: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_lernziele',
                {
                    modul: `/api/module/${modulId}`,
                    lernziele: newLernziele
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveAngebotsfrequenz(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_angebotsfrequenz',
                {
                    modul: `/api/module/${modulId}`,
                    angebotsfrequenz: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveModulInhalte(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_inhalte',
                {
                    modul: `/api/module/${modulId}`,
                    inhalte: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveVoraussetzungPruefung(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_voraussetzung_pruefungszulassung',
                {
                    modul: `/api/module/${modulId}`,
                    voraussetzungPruefungsZulassung: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveVoraussetzungEcts(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_voraussetzung_vergabe_ects',
                {
                    modul: `/api/module/${modulId}`,
                    voraussetzungenVergabeECTS: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveMedienform(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_medienformen',
                {
                    modul: `/api/module/${modulId}`,
                    medienformen: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveDokumentlinks(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_dokumentlinks',
                {
                    modul: `/api/module/${modulId}`,
                    dokumentlinks: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveLiteratur(modulId: string, newValue: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_literatur',
                {
                    modul: `/api/module/${modulId}`,
                    literatur: newValue
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveGefoerderteKompetenzen(modulId: string, payload: object): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_gefoerderte_kompetenzen',
                {
                    modul: `/api/module/${modulId}`,
                    ...payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveModulstruktur(modulId: string, payload: any): Promise<void> {
        const modulstruktur: any = {
            lehrformen: payload
        }

        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_modulstruktur',
                {
                    modul: `/api/module/${modulId}`,
                    modulstruktur: modulstruktur
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveEcts(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_ects',
                {
                    modul: `/api/module/${modulId}`,
                    ects: payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveSws(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_sws',
                {
                    modul: `/api/module/${modulId}`,
                    sws: payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveModultyp(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_modultyp',
                {
                    modul: `/api/module/${modulId}`,
                    modulTyp: payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveEmpfohlenesFachsemester(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_empfohlenes_fachsemester',
                {
                    modul: `/api/module/${modulId}`,
                    empfohlenesFachsemester: payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveLehrsprache(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_lehrsprache',
                {
                    modul: `/api/module/${modulId}`,
                    lehrsprache: payload
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveFachbereich(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_fachbereich',
                {
                    modul: `/api/module/${modulId}`,
                    fachbereichId: payload ?? ''
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function saveModulName(modulId: string, payload: string): Promise<void> {
        await setLoading('saving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/edit_name',
                {
                    modul: `/api/module/${modulId}`,
                    modulName: payload ?? ''
                }
            )
            replaceModuleInState(response.data)
        })
    }

    async function archiveModul(modulId: string): Promise<Module> {
        return setLoading('archiving', async () => {
            const response: AxiosResponse<Module, any> = await apiClient.post(
                '/api/module/archive',
                {
                    modul: `/api/module/${modulId}`
                }
            )
            replaceModuleInState(response.data)
            return response.data
        })
    }

    async function createModul(
        modulname: string,
        studiengangId: string,
        semester: string,
    ): Promise<Module> {
        return setLoading('create-modul', async () => {
            const response: AxiosResponse<Module> = await apiClient.post(
                '/api/module/create',
                {
                    modulname,
                    studiengang: `/api/studiengaenge/${studiengangId}`,
                    semester,
                },
                { headers: { 'Content-Type': 'application/ld+json' } },
            )
            modules.value = [...modules.value, response.data]
            return response.data
        })
    }

    return {
        modules,
        totalItems,
        isLoading,
        isMethodLoading,
        errors,
        hasError,
        getModules,
        getModuleById,
        getModulesByStudiengangId,
        fetchModules,
        fetchModulesByModulhandbuchId,
        fetchPaginatedModules,
        createModul,
        archiveModul,
        saveModulverantwortliche,
        saveLehrende,
        saveVeranstaltungsOrt,
        saveBenotung,
        saveZwingendeVoraussetzungen,
        saveEmpfohleneVoraussetzungen,
        saveLernziele,
        saveAngebotsfrequenz,
        saveModulInhalte,
        saveVoraussetzungPruefung,
        saveVoraussetzungEcts,
        saveMedienform,
        saveDokumentlinks,
        saveLiteratur,
        saveGefoerderteKompetenzen,
        saveModulstruktur,
        saveModulDauer,
        saveEcts,
        saveSws,
        saveEmpfohlenesFachsemester,
        saveModultyp,
        saveLehrsprache,
        saveFachbereich,
        saveModulName
    }
})
